/*
 * TYOHAAR variant picker.
 *
 * A small panel of variants and prices, rendered with the card, so opening it
 * costs nothing — no fetch, no spinner, no product page pulled into a box.
 *
 * Adding goes through window.tyohAddToBag (card-add.js), which is Dawn's cart
 * pipeline. Nothing about carts or variants is reimplemented here.
 */
if (!customElements.get('tyoh-variant-picker')) {
  customElements.define(
    'tyoh-variant-picker',
    class TyohVariantPicker extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.panel = this.querySelector('.tvp__panel');
        this.submit = this.querySelector('[data-tvp-add]');
        if (!this.panel || !this.submit) return;

        /*
          Moved to <body> once. The card clips its media box and animates the
          bar with a transform, either of which would trap a fixed-position
          panel inside the card.
        */
        document.body.appendChild(this);

        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

        this.querySelector('[data-tvp-close]')?.addEventListener('click', () => this.close());
        this.submit.addEventListener('click', this.onAdd.bind(this));
        // Clicking the backdrop, but not the panel itself.
        this.addEventListener('mousedown', (event) => {
          if (event.target === this) this.close();
        });
      }

      get selected() {
        return this.querySelector('.tvp__radio:checked');
      }

      open(opener) {
        this.opener = opener;
        this.hidden = false;
        // Two frames: the element must be laid out before the class that
        // transitions it is added, or it appears fully open.
        requestAnimationFrame(() => this.setAttribute('data-open', ''));
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onDocumentKeydown);

        if (typeof trapFocus === 'function') trapFocus(this, this.panel);
        else this.panel.focus();
      }

      /*
        returnFocus is false after a successful add.

        The cart drawer opens on top and takes focus, so handing it back to the
        card's button as well is both wrong and visible: the button regains
        focus, the card matches :focus-within, and the "Choose options" bar
        stays out until the shopper hovers the card and leaves again just to
        clear it. Cancelling still returns focus, because then there is nowhere
        else for it to go.
      */
      close({ returnFocus = true } = {}) {
        if (this.hidden) return;
        this.removeAttribute('data-open');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onDocumentKeydown);

        // Always drop the trap; whether focus moves is decided here, not by it.
        if (typeof removeTrapFocus === 'function') removeTrapFocus();

        if (returnFocus) {
          // preventScroll: the card is already on screen, and focus() would
          // otherwise scroll it into view and shift the image.
          this.opener?.focus({ preventScroll: true });
        } else if (this.contains(document.activeElement)) {
          // Focus is still inside a panel that is about to be hidden, and a
          // hidden element holding focus sends it to <body> unpredictably.
          document.activeElement.blur();
        }

        // Hidden only after the fade, so it does not vanish mid-transition.
        const done = () => {
          this.hidden = true;
        };
        const ms = parseFloat(getComputedStyle(this).getPropertyValue('--tvp-speed')) || 0;
        if (ms > 0) window.setTimeout(done, ms);
        else done();
      }

      onDocumentKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
      }

      setLoading(isLoading) {
        this.submit.classList.toggle('is-loading', isLoading);
        this.submit.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        this.submit.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      async onAdd() {
        const chosen = this.selected;
        if (!chosen || this.loading) return;

        this.loading = true;
        this.setLoading(true);
        try {
          await window.tyohAddToBag({ variantId: chosen.value, opener: this.opener });
          this.close({ returnFocus: false });
        } catch (error) {
          console.error('[variant-picker]', error);
        } finally {
          this.loading = false;
          this.setLoading(false);
        }
      }
    }
  );
}

/*
 * The card's "Choose options" bar. A plain delegated listener rather than an
 * element per card: the grid gains cards as Load more runs, and delegation
 * covers those without any re-binding.
 */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-tvp-open]');
  if (!opener) return;

  // The card's title link is stretched over the whole card.
  event.preventDefault();
  event.stopPropagation();

  const picker = document.getElementById(opener.dataset.tvpOpen);
  if (picker && typeof picker.open === 'function') picker.open(opener);
  else window.location = opener.dataset.productUrl || opener.getAttribute('href');
});
